import React, { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import EngineerMapView from "./EngineerMapView";
import { fetchEngineerMapLocations } from "../../services/api/workforce";

// ─── Engineer list item ───────────────────────────────────────────────────────
const EngineerItem = ({ eng, isSelected, onClick }) => {
  const initials = eng.name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all"
      style={{
        background: isSelected ? "#EEF2FF" : "transparent",
        border: isSelected ? "1px solid #c7d2fe" : "1px solid transparent",
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.background = "#F8FAFF";
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.background = "transparent";
      }}
    >
      {eng.profileUrl ? (
        <img
          src={eng.profileUrl}
          alt={eng.name}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          style={{ border: isSelected ? "2px solid #4F7FEF" : "2px solid #e2e8f0" }}
        />
      ) : (
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
          style={{
            background: isSelected ? "#c7d2fe" : "#e2e8f0",
            color: isSelected ? "#3730a3" : "#64748b",
            border: isSelected ? "2px solid #4F7FEF" : "2px solid transparent",
          }}
        >
          {initials}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p
          className="text-[13px] font-semibold truncate"
          style={{ color: isSelected ? "#1e40af" : "#1e293b" }}
        >
          {eng.name}
        </p>
        {eng.city || eng.country ? (
          <p className="text-[11px] text-gray-400 truncate flex items-center gap-1 mt-0.5">
            <i className="fa-solid fa-location-dot text-[9px]" />
            {[eng.city, eng.country].filter(Boolean).join(", ")}
          </p>
        ) : (
          <p className="text-[11px] text-gray-300 mt-0.5 italic">No location</p>
        )}
      </div>
      {isSelected && (
        <i className="fa-solid fa-chevron-right text-[10px] text-indigo-400 flex-shrink-0" />
      )}
    </button>
  );
};

// ─── Main dashboard ───────────────────────────────────────────────────────────
const WorkForceDashboard = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["engineer-map-locations"],
    queryFn: fetchEngineerMapLocations,
    staleTime: 300_000,
  });

  const engineers = useMemo(() => (Array.isArray(data) ? data : (data?.data ?? [])), [data]);

  const countries = useMemo(() => {
    const set = new Set(engineers.map((e) => e.country).filter(Boolean));
    return Array.from(set).sort();
  }, [engineers]);

  const filtered = useMemo(() => {
    let list = engineers;
    if (selectedCountry) list = list.filter((e) => e.country === selectedCountry);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.city?.toLowerCase().includes(q) ||
          e.country?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [engineers, search, selectedCountry]);

  const handleSelect = (eng) => {
    setSelected(eng);
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelected((prev) => {
      if (!prev) return null;
      if (country && prev.country !== country) return null;
      return prev;
    });
  };

  return (
    <>
      <div
        className={`bg-[#F6F7FB] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full flex flex-col overflow-hidden transition-all ${
          isBannerVisible ? "top-[106px] h-[calc(100vh-106px)]" : "top-[56px] h-[calc(100vh-56px)]"
        }`}
      >
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="border-b border-zinc-200 bg-white flex-shrink-0">
          <BaseWrapper>
            <div className="flex items-center gap-3 py-[7px] flex-wrap">
              <h3 className="font-light">Work Force Map</h3>
            </div>
          </BaseWrapper>
        </div>

        <BaseWrapper className="py-4 flex flex-col flex-1 min-h-0">
          {/* ── Map + sidebar ────────────────────────────────────────────────── */}
          <div
            className="bg-white rounded-xl border border-zinc-200 flex overflow-hidden flex-1"
            style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
          >
            {/* Map */}
            <div className="flex-1 h-full" style={{ minWidth: 0 }}>
              <EngineerMapView engineers={filtered} selected={selected} onSelect={handleSelect} />
            </div>

            {/* Sidebar */}
            <div
              className="flex flex-col border-l border-zinc-100 overflow-hidden"
              style={{ width: 272, flexShrink: 0 }}
            >
              {/* Sidebar header */}
              <div className="px-3 pt-3 pb-2 border-b border-zinc-100 flex flex-col gap-2">
                <p className="text-xs font-semibold text-gray-600">
                  Engineers <span className="text-gray-400 font-normal">({filtered.length})</span>
                </p>
                <div className="relative">
                  <i className="fa-light fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name or location…"
                    className="w-full pl-7 pr-3 py-1.5 text-xs rounded-lg border border-zinc-200 bg-gray-50 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </div>
                <div className="relative">
                  <i className="fa-solid fa-globe absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[11px] pointer-events-none" />
                  <select
                    value={selectedCountry}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className="w-full pl-7 pr-6 py-1.5 text-xs rounded-lg border border-zinc-200 bg-gray-50 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 appearance-none cursor-pointer"
                  >
                    <option value="">All Countries</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <i className="fa-solid fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[9px] pointer-events-none" />
                </div>
              </div>

              {/* Engineer list */}
              <div className="flex-1 overflow-y-auto p-2">
                {isLoading ? (
                  <div className="flex flex-col gap-2 mt-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                        <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                        <div className="flex-1">
                          <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4 mb-1.5" />
                          <div className="h-2.5 bg-gray-100 rounded animate-pulse w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                    <i className="fa-solid fa-user-slash text-2xl mb-2" />
                    <p className="text-xs">No engineers found</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {filtered.map((eng) => (
                      <EngineerItem
                        key={eng.id}
                        eng={eng}
                        isSelected={selected?.id === eng.id}
                        onClick={() => handleSelect(eng)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </BaseWrapper>
      </div>
    </>
  );
};

export default WorkForceDashboard;
